<template>
    <div style="padding: 20px;">
      <h2>🎙️ 音频录制</h2>
      
      <div class="record-mode">
        <label class="switch">
          <input type="checkbox" v-model="isSystemAudio">
          <span class="slider round"></span>
        </label>
        <span class="mode-text">{{ isSystemAudio ? '系统内录音' : '麦克风录音' }}</span>
        <div class="mode-tip">
          <span v-if="isSystemAudio">将录制您的设备播放的声音（如视频、音乐等）</span>
          <span v-else>将录制您的麦克风输入（如人声）</span>
        </div>
      </div>
      
      <div class="button-group">
        <button @click="startRecording" :disabled="recording" class="primary-btn">
          <span v-if="!recording">▶️ 开始录音</span>
          <span v-else>⏳ 录音中...</span>
        </button>
        <button @click="stopRecording" :disabled="!recording" class="stop-btn">
          ⏹️ 停止录音
        </button>
      </div>
      
      <div v-if="errorMessage" class="error-message">
        ⚠️ {{ errorMessage }}
        <div v-if="showHelp" class="help-tips">
          <p><b>提示：</b></p>
          <ul>
            <li v-if="isSystemAudio">请确保在权限弹窗中选择"共享系统音频"</li>
            <li v-else>请确保已授予麦克风访问权限</li>
            <li>尝试使用 Chrome 或 Edge 浏览器</li>
            <li v-if="isSystemAudio">尝试共享整个屏幕而不是单个窗口</li>
            <li v-if="isSystemAudio">如使用Mac，请确保浏览器有屏幕录制权限</li>
          </ul>
        </div>
      </div>
  
      <div v-if="audioUrl" class="audio-result">
        <h3>🔊 录音结果：</h3>
        <audio :src="audioUrl" controls></audio>
        <br />
        <a :href="audioUrl" :download="'recording_' + new Date().getTime() + '.wav'" class="download-link">
          📥 下载录音
        </a>
      </div>
      
      <div v-if="recording" class="recording-indicator">
        <div class="pulse"></div>
        录制中... {{ recordingTime }}秒
        <div class="recording-mode">
          <span v-if="isSystemAudio">系统内录音模式</span>
          <span v-else>麦克风录音模式</span>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'AudioRecorder',
    data() {
      return {
        isSystemAudio: false,
        audioContext: null,
        mediaStream: null,
        audioSource: null,
        audioDestination: null,
        audioProcessor: null,
        audioChunks: [],
        mediaRecorder: null,
        audioUrl: null,
        recording: false,
        errorMessage: '',
        showHelp: false,
        startTime: 0,
        recordingTime: 0,
        recordingTimer: null,
        bufferSize: 4096,
      };
    },
    methods: {
      async startRecording() {
        this.errorMessage = '';
        this.showHelp = false;
        this.audioUrl = null;
        this.audioChunks = [];
        
        try {
          if (this.isSystemAudio) {
            await this.startSystemAudioRecording();
          } else {
            await this.startMicrophoneRecording();
          }
          
          // 开始记录时间
          this.recording = true;
          this.startTime = Date.now();
          this.recordingTimer = setInterval(() => {
            this.recordingTime = Math.floor((Date.now() - this.startTime) / 1000);
          }, 1000);
          
          console.log(`开始录制${this.isSystemAudio ? '系统' : '麦克风'}音频`);
        } catch (err) {
          console.error('录制出错:', err);
          this.errorMessage = err.message || `无法录制${this.isSystemAudio ? '系统' : '麦克风'}音频，请检查权限设置`;
          this.showHelp = true;
          this.cleanup();
        }
      },
      
      async startSystemAudioRecording() {
        // 检查浏览器兼容性
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          throw new Error('您的浏览器不支持 Web Audio API，请尝试使用 Chrome 或 Edge 的最新版本');
        }
        
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
          throw new Error('您的浏览器不支持系统音频录制，请尝试使用 Chrome 或 Edge 的最新版本');
        }
        
        // 尝试先获取麦克风权限，这有助于初始化音频系统
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('已获取麦克风权限，这有助于系统音频捕获');
        } catch (e) {
          console.log('未获取麦克风权限，继续尝试系统音频捕获');
        }
        
        // 创建音频上下文
        this.audioContext = new AudioContext({
          latencyHint: 'interactive',
          sampleRate: 44100
        });
        
        console.log('请求系统音频访问权限...');
        
        // 显示更详细的用户指导
        this.errorMessage = '请在接下来的对话框中选择"共享"，并确保勾选"共享系统音频"选项';
        this.showHelp = true;
        
        // 请求屏幕共享并包含音频 - 使用更宽松的约束
        this.mediaStream = await navigator.mediaDevices.getDisplayMedia({
          video: true, // 简化视频约束
          audio: {
            // 移除可能限制音频捕获的约束
            suppressLocalAudioPlayback: false
          }
        });
        
        // 检查是否获取到音频轨道
        const audioTracks = this.mediaStream.getAudioTracks();
        if (audioTracks.length === 0) {
          // 如果没有获取到音频轨道，尝试第二种方法 - 针对 macOS
          console.log('第一次尝试未获取到音频轨道，正在尝试备用方法...');
          
          // 释放第一次尝试的资源
          this.mediaStream.getTracks().forEach(track => track.stop());
          
          try {
            // 对于 macOS，有时需要更特殊的处理
            this.mediaStream = await navigator.mediaDevices.getDisplayMedia({
              video: {
                displaySurface: 'monitor', // 明确要求整个屏幕
                logicalSurface: true,
                cursor: 'always'
              },
              audio: true // 简化音频约束
            });
            
            // 再次检查音频轨道
            const secondTryAudioTracks = this.mediaStream.getAudioTracks();
            if (secondTryAudioTracks.length === 0) {
              throw new Error('未检测到音频轨道，请确保已勾选"共享系统音频"选项。如果使用 Mac，请确保在系统偏好设置中授予浏览器屏幕录制权限');
            }
            
            console.log('成功获取系统音频轨道(第二次尝试):', secondTryAudioTracks[0].label);
          } catch (secondError) {
            throw new Error('无法捕获系统音频。如果使用 macOS，请在"系统偏好设置→安全性与隐私→隐私→屏幕录制"中允许浏览器录制屏幕，然后重新启动浏览器');
          }
        } else {
          console.log('成功获取系统音频轨道:', audioTracks[0].label);
        }
        
        // 清除指导消息
        this.errorMessage = '';
        this.showHelp = false;
        
        // 创建音频节点
        this.audioSource = this.audioContext.createMediaStreamSource(this.mediaStream);
        this.audioDestination = this.audioContext.createMediaStreamDestination();
        
        // 创建 ScriptProcessor 用于处理音频数据
        this.audioProcessor = this.audioContext.createScriptProcessor(this.bufferSize, 2, 2);
        
        // 添加音量检测和可视化
        let silenceCounter = 0;
        const silenceThreshold = 0.01;
        
        // 设置音频处理回调
        this.audioProcessor.onaudioprocess = (e) => {
          if (!this.recording) return;
          
          // 收集音频数据
          const left = e.inputBuffer.getChannelData(0).slice();
          const right = e.inputBuffer.getChannelData(1).slice();
          
          // 检查是否有声音(简单的音量检测)
          let sumSquares = 0;
          for (let i = 0; i < left.length; i++) {
            sumSquares += left[i] * left[i];
          }
          const rms = Math.sqrt(sumSquares / left.length);
          
          if (rms < silenceThreshold) {
            silenceCounter++;
            // 连续30次检测到静音(约3秒)，显示警告
            if (silenceCounter > 30 && silenceCounter % 30 === 0) {
              console.warn('检测到持续静音，可能未正确捕获系统音频');
              // 避免重复警告
              if (!this.errorMessage) {
                this.errorMessage = '没有检测到音频信号。请确保您的系统正在播放声音，并且已允许捕获系统音频';
                this.showHelp = true;
                
                // 3秒后自动清除消息
                setTimeout(() => {
                  if (this.errorMessage === '没有检测到音频信号。请确保您的系统正在播放声音，并且已允许捕获系统音频') {
                    this.errorMessage = '';
                    this.showHelp = false;
                  }
                }, 3000);
              }
            }
          } else {
            // 重置静音计数器
            silenceCounter = 0;
            // 如果有声音且有警告，清除警告
            if (this.errorMessage === '没有检测到音频信号。请确保您的系统正在播放声音，并且已允许捕获系统音频') {
              this.errorMessage = '';
              this.showHelp = false;
            }
          }
          
          this.audioChunks.push({ left, right });
        };
        
        // 连接音频处理管道
        this.audioSource.connect(this.audioProcessor);
        this.audioProcessor.connect(this.audioDestination);
        this.audioProcessor.connect(this.audioContext.destination);
        
        // 设置停止处理程序
        const videoTrack = this.mediaStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.onended = () => {
            console.log('用户停止了共享');
            if (this.recording) {
              this.stopRecording();
            }
          };
        }
      },
      
      async startMicrophoneRecording() {
        try {
          console.log('请求麦克风访问权限...');
          
          // 请求麦克风访问权限
          this.mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          });
          
          console.log('成功获取麦克风音频轨道');
          
          // 使用 MediaRecorder API 录制麦克风
          // 尝试创建 MediaRecorder，选择兼容的 MIME 类型
          try {
            if (MediaRecorder.isTypeSupported('audio/webm')) {
              this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'audio/webm' });
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
              this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'audio/mp4' });
            } else {
              this.mediaRecorder = new MediaRecorder(this.mediaStream);
            }
          } catch (err) {
            console.error('创建 MediaRecorder 失败，使用默认配置:', err);
            this.mediaRecorder = new MediaRecorder(this.mediaStream);
          }
          
          // 设置数据处理
          this.mediaRecorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
              this.audioChunks.push(e.data);
            }
          };
          
          // 启动 MediaRecorder
          this.mediaRecorder.start(100);
        } catch (err) {
          throw new Error(`无法访问麦克风: ${err.message}`);
        }
      },
      
      stopRecording() {
        if (!this.recording) return;
        
        try {
          this.recording = false;
          clearInterval(this.recordingTimer);
          console.log('停止录制，录制时长:', this.recordingTime, '秒');
          
          if (this.isSystemAudio) {
            this.processSystemAudioData();
          } else {
            this.processMicrophoneData();
          }
        } catch (error) {
          console.error('停止录制出错:', error);
          this.errorMessage = `停止录制失败: ${error.message}`;
          this.cleanup();
        }
      },
      
      processMicrophoneData() {
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
          // 手动停止 MediaRecorder
          this.mediaRecorder.stop();
          
          // 等待数据处理完成
          this.mediaRecorder.onstop = () => {
            if (this.audioChunks.length === 0) {
              this.errorMessage = '没有录制到任何音频数据';
              this.showHelp = true;
              this.cleanup();
              return;
            }
            
            // 创建 Blob 并设置 URL
            const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
            this.audioUrl = URL.createObjectURL(blob);
            console.log('麦克风音频处理完成，大小:', (blob.size / 1024).toFixed(2), 'KB');
            this.cleanup();
          };
        } else {
          this.errorMessage = '录音器未正确初始化';
          this.showHelp = true;
          this.cleanup();
        }
      },
      
      processSystemAudioData() {
        try {
          if (this.audioChunks.length === 0) {
            this.errorMessage = '没有录制到任何音频数据';
            this.showHelp = true;
            this.cleanup();
            return;
          }
          
          // 创建 WAV 文件
          const sampleRate = this.audioContext.sampleRate;
          const numChannels = 2;
          const bitsPerSample = 16;
          
          console.log('处理系统音频数据，采样率:', sampleRate, 'Hz');
          
          // 合并所有音频块
          const dataLength = this.audioChunks.reduce((acc, chunk) => acc + chunk.left.length, 0);
          const leftBuffer = new Float32Array(dataLength);
          const rightBuffer = new Float32Array(dataLength);
          
          let offset = 0;
          for (const chunk of this.audioChunks) {
            leftBuffer.set(chunk.left, offset);
            rightBuffer.set(chunk.right, offset);
            offset += chunk.left.length;
          }
          
          // 将音频数据转换为 16 位整数
          const interleavedData = new Int16Array(leftBuffer.length * numChannels);
          for (let i = 0; i < leftBuffer.length; i++) {
            // 左声道
            interleavedData[i * 2] = Math.max(-1, Math.min(1, leftBuffer[i])) * 0x7FFF;
            // 右声道
            interleavedData[i * 2 + 1] = Math.max(-1, Math.min(1, rightBuffer[i])) * 0x7FFF;
          }
          
          // 创建 WAV 头
          const dataSize = interleavedData.length * (bitsPerSample / 8);
          const buffer = new ArrayBuffer(44 + dataSize);
          const view = new DataView(buffer);
          
          // RIFF 标识
          this.writeString(view, 0, 'RIFF');
          view.setUint32(4, 36 + dataSize, true);
          this.writeString(view, 8, 'WAVE');
          
          // fmt 子块
          this.writeString(view, 12, 'fmt ');
          view.setUint32(16, 16, true);
          view.setUint16(20, 1, true);
          view.setUint16(22, numChannels, true);
          view.setUint32(24, sampleRate, true);
          view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
          view.setUint16(32, numChannels * (bitsPerSample / 8), true);
          view.setUint16(34, bitsPerSample, true);
          
          // data 子块
          this.writeString(view, 36, 'data');
          view.setUint32(40, dataSize, true);
          
          // 写入实际音频数据
          const uint8 = new Uint8Array(buffer, 44);
          const dataBytes = new Uint8Array(interleavedData.buffer);
          uint8.set(dataBytes);
          
          // 创建 Blob 并设置 URL
          const blob = new Blob([buffer], { type: 'audio/wav' });
          this.audioUrl = URL.createObjectURL(blob);
          
          console.log('系统音频处理完成，大小:', (blob.size / 1024).toFixed(2), 'KB');
        } catch (error) {
          console.error('处理音频数据失败:', error);
          this.errorMessage = `处理音频失败: ${error.message}`;
          this.showHelp = true;
        } finally {
          this.cleanup();
        }
      },
      
      writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      },
      
      cleanup() {
        console.log('清理资源');
        
        // 麦克风录音清理
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
          try {
            this.mediaRecorder.stop();
          } catch (e) {
            console.error('停止 MediaRecorder 失败:', e);
          }
        }
        
        // 系统音频录音清理
        if (this.audioProcessor) {
          try {
            this.audioProcessor.disconnect();
          } catch (e) {
            console.error('断开 AudioProcessor 失败:', e);
          }
          this.audioProcessor = null;
        }
        
        if (this.audioSource) {
          try {
            this.audioSource.disconnect();
          } catch (e) {
            console.error('断开 AudioSource 失败:', e);
          }
          this.audioSource = null;
        }
        
        // 关闭音频上下文
        if (this.audioContext && this.audioContext.state !== 'closed') {
          try {
            this.audioContext.close();
          } catch (e) {
            console.error('关闭音频上下文失败:', e);
          }
        }
        
        // 停止所有媒体轨道
        if (this.mediaStream) {
          this.mediaStream.getTracks().forEach(track => {
            try {
              track.stop();
            } catch (e) {
              console.error('停止媒体轨道失败:', e);
            }
          });
          this.mediaStream = null;
        }
        
        this.recordingTime = 0;
        clearInterval(this.recordingTimer);
      }
    },
    beforeUnmount() {
      this.stopRecording();
      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl);
      }
    }
  };
  </script>
  
  <style scoped>
  .button-group {
    margin-bottom: 20px;
  }
  
  button {
    padding: 10px 20px;
    margin-right: 10px;
    font-size: 16px;
    cursor: pointer;
    border: none;
    border-radius: 4px;
    transition: background-color 0.3s;
  }
  
  .primary-btn {
    background-color: #4CAF50;
    color: white;
  }
  
  .primary-btn:hover {
    background-color: #45a049;
  }
  
  .stop-btn {
    background-color: #f44336;
    color: white;
  }
  
  .stop-btn:hover {
    background-color: #d32f2f;
  }
  
  button:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  
  .error-message {
    margin-top: 15px;
    padding: 10px;
    background-color: #ffebee;
    border-left: 4px solid #f44336;
    color: #d32f2f;
    border-radius: 4px;
  }
  
  .help-tips {
    margin-top: 10px;
    font-size: 14px;
    color: #333;
  }
  
  .help-tips ul {
    margin-top: 5px;
    padding-left: 20px;
  }
  
  .audio-result {
    margin-top: 20px;
    padding: 15px;
    background-color: #e8f5e9;
    border-radius: 4px;
  }
  
  .download-link {
    display: inline-block;
    margin-top: 10px;
    color: #2196F3;
    text-decoration: none;
  }
  
  .download-link:hover {
    text-decoration: underline;
  }
  
  .recording-indicator {
    display: flex;
    align-items: center;
    margin-top: 15px;
    color: #f44336;
    font-weight: bold;
  }
  
  .recording-mode {
    margin-left: 10px;
    font-size: 12px;
    background-color: #ffebee;
    padding: 2px 6px;
    border-radius: 10px;
  }
  
  .pulse {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #f44336;
    margin-right: 8px;
    animation: pulse 1s infinite;
  }
  
  @keyframes pulse {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .record-mode {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  
  .mode-text {
    margin-left: 10px;
    font-weight: bold;
  }
  
  .mode-tip {
    margin-left: 10px;
    color: #666;
    font-size: 14px;
  }
  
  /* 滑动开关样式 */
  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }
  
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
  }
  
  input:checked + .slider {
    background-color: #2196F3;
  }
  
  input:focus + .slider {
    box-shadow: 0 0 1px #2196F3;
  }
  
  input:checked + .slider:before {
    transform: translateX(26px);
  }
  
  .slider.round {
    border-radius: 34px;
  }
  
  .slider.round:before {
    border-radius: 50%;
  }
  </style>
  